<template>
  <div :class="$style.wrapper">
    <form :class="$style.form" @submit.prevent="handleSubmit">
      <!-- Название модели -->
      <div :class="$style.fieldGroup">
        <div :class="$style.sectionLabel">Название модели или артикул</div>
        <div :class="$style.inputCard">
          <input
            :value="displayModelTitle"
            type="text"
            placeholder="Выберите модель"
            readonly
            :class="$style.modelInput"
            @click="openModelSelect"
            @focus.prevent="openModelSelect"
          />
        </div>
      </div>

      <!-- Галерея изображений модели (только при выборе из справочника) -->
      <div
        v-if="!customModelMode && modelImages && modelImages.length > 0"
        :class="$style.galleryWrap"
      >
        <ProductGallerySimple
          :images="modelImages"
          :alt-base="displayModelTitle || 'Фото модели'"
        />
      </div>

      <!-- «Своя модель»: поля, которых нет в справочнике. Показываются на этом
           же экране под полем выбора модели. is_trade/has_box и т.д. не дублируем —
           они в варианте товара ниже. -->
      <div v-if="customModelMode" :class="$style.customModelSection">
        <div :class="$style.customModelHeader">
          Данные модели
        </div>

        <div :class="$style.fieldGroup">
          <div :class="$style.sectionLabel">Название *</div>
          <div :class="$style.inputCard">
            <input
              v-model.trim="customModel.title"
              type="text"
              placeholder="Например: Nike Air Max 90"
              maxlength="200"
              :class="$style.modelInput"
            />
          </div>
        </div>

        <div :class="$style.fieldGroup">
          <div :class="$style.sectionLabel">Артикул *</div>
          <div :class="$style.inputCard">
            <input
              v-model.trim="customModel.article"
              type="text"
              placeholder="Например: 366732-001"
              maxlength="100"
              :class="$style.modelInput"
            />
          </div>
        </div>

        <div :class="$style.fieldGroup">
          <div :class="$style.sectionLabel">Бренд *</div>
          <div :class="$style.inputCard">
            <input
              v-model.trim="customModel.brand"
              type="text"
              placeholder="Например: Nike"
              maxlength="100"
              :class="$style.modelInput"
            />
          </div>
        </div>

        <div :class="$style.fieldGroup">
          <div :class="$style.sectionLabel">Категория *</div>
          <div :class="$style.inputCard">
            <select v-model="customModel.category" :class="$style.modelInput">
              <option value="" disabled>Выберите категорию</option>
              <option value="MALE">Мужские (MALE)</option>
              <option value="FEMALE">Женские (FEMALE)</option>
              <option value="UNISEX">Унисекс (UNISEX)</option>
            </select>
          </div>
        </div>

        <div :class="$style.fieldGroup">
          <div :class="$style.sectionLabel">Цвет *</div>
          <div :class="$style.inputCard">
            <input
              v-model.trim="customModel.colour"
              type="text"
              placeholder="Например: Чёрный"
              maxlength="100"
              :class="$style.modelInput"
            />
          </div>
        </div>

        <!-- Фото модели: UI как в «Кастомные фото» (квадратные превью с +) -->
        <div :class="$style.customImagesSection">
          <div :class="$style.customImagesLabel">Фото модели * (мин. 1)</div>
          <p :class="$style.customImagesRequirements">
            Загрузите фотографии модели. Форматы: JPG, PNG или WebP. До 5 изображений.
          </p>
          <input
            ref="modelImageInput"
            type="file"
            accept="image/*"
            multiple
            :class="$style.hiddenInput"
            @change="handleModelImageChange"
          />
          <div :class="$style.customImagesGrid">
            <!-- Уже выбранные фото -->
            <div
              v-for="(img, idx) in customModel.images"
              :key="idx"
              :class="$style.customImageWrapper"
            >
              <div :class="$style.customImagePreview">
                <img :src="getImagePreview(img)" :alt="`Фото модели ${idx + 1}`" />
                <button
                  type="button"
                  :class="$style.removeImageBtn"
                  @click="removeModelImage(idx)"
                >
                  <img
                    src="@/assets/img/account/my-products/delete.svg"
                    alt="Удалить"
                  />
                </button>
              </div>
            </div>
            <!-- Кнопка добавления (показываем, пока не достигнут лимит 5) -->
            <div
              v-if="customModel.images.length < 5"
              :class="$style.customImageWrapper"
            >
              <button
                type="button"
                :class="$style.addImageBtn"
                @click="triggerModelImageInput"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Список вариантов (карточки) -->
      <section :class="$style.variantsSection">
        <div
          v-for="(variant, index) in variants"
          :key="variant.id"
          :class="$style.variantCard"
        >
          <div :class="$style.variantTopRow">
            <!-- Размер -->
            <div :class="$style.variantInputBlock">
              <div :class="[$style.combinedInput, $style.sizeCombined]">
                <div :class="$style.sizeInputWrapper">
                  <input
                    ref="sizeInputs"
                    type="text"
                    v-model.trim="variant.size"
                    placeholder="0"
                    :class="[$style.invisibleInput, $style.sizeInput]"
                    :style="{
                      width: variant.size
                        ? `${String(variant.size).length}ch`
                        : `1ch`,
                    }"
                    required
                    @blur="normalizeSize(variant)"
                    @input="hideSizeAlert(variant)"
                  />
                  <div v-if="variant.showSizeAlert" :class="$style.sizeAlert">
                    {{ getSizeAlertMessage(variant.size_type) }}
                  </div>
                </div>
                <div :class="$style.sizeTypeWrapper">
                  <span
                    v-if="variant.size_type"
                    :class="$style.sizeTypeLabel"
                    @click="openSizeTypeDropdown(variant.id, $event)"
                  >
                    {{ getSizeTypeLabel(variant.size_type) }}
                    <svg
                      :class="[
                        $style.sizeTypeArrow,
                        openDropdownId === variant.id &&
                          $style.sizeTypeArrowOpen,
                      ]"
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <Teleport to="body">
                    <div
                      v-if="
                        openDropdownId === variant.id ||
                        dropdownClosingId === variant.id
                      "
                      :class="[
                        $style.sizeTypeDropdown,
                        dropdownClosingId === variant.id &&
                          $style.sizeTypeDropdownClosing,
                      ]"
                      :style="dropdownStyle"
                      @click.stop
                    >
                      <button
                        v-for="type in sizeTypes"
                        :key="type"
                        type="button"
                        :class="[
                          $style.sizeTypeOption,
                          variant.size_type === type &&
                            $style.sizeTypeOptionActive,
                        ]"
                        @click="selectSizeType(variant.id, type)"
                      >
                        {{ getSizeTypeLabel(type) }}
                      </button>
                    </div>
                  </Teleport>
                </div>
              </div>
            </div>

            <!-- Кол-во -->
            <div :class="$style.variantInputBlock">
              <div :class="[$style.combinedInput, $style.countCombined]">
                <input
                  ref="countInputs"
                  v-model="variant.count"
                  inputmode="numeric"
                  placeholder="0"
                  :class="[$style.invisibleInput, $style.countInput]"
                  required
                />
                <span>пар</span>
              </div>
            </div>

            <!-- Стоимость -->
            <div :class="[$style.variantInputBlock, $style.priceBlock]">
              <div :class="[$style.combinedInput, $style.priceCombined]">
                <input
                  ref="priceInputs"
                  v-model="variant.price"
                  inputmode="numeric"
                  placeholder="0"
                  :class="[$style.invisibleInput, $style.priceInput]"
                  required
                />
                <span>₽</span>
              </div>
              <div
                v-if="hasSize(variant) && getAvgPrice(variant)"
                :class="$style.avgPriceHint"
              >
                Средняя цена: {{ formatPrice(getAvgPrice(variant)) }} ₽
              </div>
            </div>

            <!-- Удалить -->
            <button
              type="button"
              :class="$style.removeBtn"
              @click="removeVariant(index)"
            >
              <img
                src="@/assets/img/account/my-products/delete.svg"
                alt="Удалить"
              />
            </button>
          </div>

          <div :class="$style.variantDivider"></div>

          <div :class="$style.variantBottomRow">
            <div :class="$style.variantBottomItem">
              <span>Возможность торга</span>
              <label :class="$style.toggle">
                <input type="checkbox" v-model="variant.allowTrade" />
                <span></span>
              </label>
            </div>
            <div :class="$style.variantBottomItem">
              <span>Наличие коробки</span>
              <label :class="[$style.toggle, $style.toggleBlack]">
                <input type="checkbox" v-model="variant.hasBox" />
                <span></span>
              </label>
            </div>
            <div :class="$style.variantBottomItem">
              <span>Кастомные фото</span>
              <label :class="$style.toggle">
                <input type="checkbox" v-model="variant.isCustom" />
                <span></span>
              </label>
            </div>
          </div>

          <!-- Загрузка кастомных изображений -->
          <div v-if="variant.isCustom" :class="$style.customImagesSection">
            <div :class="$style.customImagesLabel">
              Фотографии кастомных кроссовок
            </div>
            <p :class="$style.customImagesRequirements">
              Кроссовки должны быть сняты на белом фоне. Форматы: JPG, PNG или WebP. Размер: от 800×800 до 4096×4096 px, не более 5 МБ на файл.
            </p>
            <div :class="$style.customImagesGrid">
              <!-- Первое изображение -->
              <div :class="$style.customImageWrapper">
                <input
                  :ref="`customImage1_${variant.id}`"
                  type="file"
                  accept="image/*"
                  :class="$style.hiddenInput"
                  @change="handleCustomImageChange(variant, 1, $event)"
                />
                <div
                  v-if="variant.customImage1"
                  :class="$style.customImagePreview"
                >
                  <img
                    :src="getImagePreview(variant.customImage1)"
                    alt="Кастомное фото 1"
                  />
                  <button
                    type="button"
                    :class="$style.removeImageBtn"
                    @click="removeCustomImage(variant, 1)"
                  >
                    <img
                      src="@/assets/img/account/my-products/delete.svg"
                      alt="Удалить"
                    />
                  </button>
                </div>
                <button
                  v-else
                  type="button"
                  :class="$style.addImageBtn"
                  @click="triggerImageInput(variant, 1)"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <!-- Второе изображение -->
              <div :class="$style.customImageWrapper">
                <input
                  :ref="`customImage2_${variant.id}`"
                  type="file"
                  accept="image/*"
                  :class="$style.hiddenInput"
                  @change="handleCustomImageChange(variant, 2, $event)"
                />
                <div
                  v-if="variant.customImage2"
                  :class="$style.customImagePreview"
                >
                  <img
                    :src="getImagePreview(variant.customImage2)"
                    alt="Кастомное фото 2"
                  />
                  <button
                    type="button"
                    :class="$style.removeImageBtn"
                    @click="removeCustomImage(variant, 2)"
                  >
                    <img
                      src="@/assets/img/account/my-products/delete.svg"
                      alt="Удалить"
                    />
                  </button>
                </div>
                <button
                  v-else
                  type="button"
                  :class="$style.addImageBtn"
                  @click="triggerImageInput(variant, 2)"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Кнопка добавления (выглядит как блок с заголовками) -->
        <div :class="$style.addVariantBtn">
          <span
            @click="addNewVariant('size')"
            :class="[$style.clickableLabel, $style.addVariantSize]"
          >
            Размер
          </span>
          <span
            @click="addNewVariant('count')"
            :class="[$style.clickableLabel, $style.addVariantCount]"
          >
            Кол-во пар
          </span>
          <span
            @click="addNewVariant('price')"
            :class="[$style.clickableLabel, $style.addVariantPrice]"
          >
            Стоимость
          </span>
        </div>
      </section>

      <div :class="$style.links">
        <button type="button" @click="openFaq">
          Перейти к FAQ
          <span :class="$style.arrowIcon">›</span>
        </button>
      </div>

      <div v-if="!hasTelegramMainButton" :class="$style.submitBar">
        <button
          type="submit"
          :disabled="!canSubmit || isSubmitting"
          :class="$style.submitBtn"
        >
          {{ isSubmitting ? "Отправляем..." : "Отправить на модерацию" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import ProductGallerySimple from "@/components/ProductGallerySimple.vue"
import {
  getSizeLimits,
  isValidSize,
  convertSize,
  findClosestSize,
  SIZE_CHART,
} from "@/utils/sizeChart.js"

const createVariant = () => ({
  id: Date.now() + Math.random(),
  size: "",
  size_type: "EU",
  _previousSizeType: "EU", // Для отслеживания предыдущего типа при смене
  count: "",
  price: "",
  allowTrade: true,
  isCustom: false,
  hasBox: true,
  showSizeAlert: false,
  customImage1: null,
  customImage2: null,
})

export default {
  name: "AddedProduct",
  components: { ProductGallerySimple },
  data() {
    return {
      form: {
        modelId: null,
      },
      variants: [],
      isSubmitting: false,
      hasTelegramMainButton: false,
      mainButtonInstance: null,
      mbHandler: null,
      openDropdownId: null,
      dropdownClosingId: null,
      dropdownPosition: { top: 0, left: 0 },
      sizeTypes: ["EU", "RU", "US", "UK", "CM"],
      avgPrices: null, // Объект с средними ценами по размерам
      // «Своя модель»: поля, которых нет в справочнике. Заполняются на этом же
      // экране под полем выбора модели (когда seller нажал «Добавить свою модель»
      // в SelectModel). is_trade/has_box и т.д. НЕ дублируем — они в вариантах.
      customModel: {
        title: "",
        article: "",
        brand: "",
        category: "",
        colour: "",
        images: [], // массив File
      },
    }
  },
  computed: {
    dropdownStyle() {
      return {
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
      }
    },
    // Режим «своя модель»: seller выбрал создание модели, которой нет в справочнике.
    customModelMode() {
      return this.$store.state.customModelMode
    },
    // Отображаемое в поле выбора модели название. В режиме «своя модель» —
    // введённое название модели (или заглушка), иначе — title выбранной модели.
    // computed вместо императивной записи в form.modelTitle — корректно при
    // перемонтировании компонента (нет гонки watchers).
    displayModelTitle() {
      if (this.customModelMode) {
        return this.customModel.title?.trim() || "Своя модель (на модерации)"
      }
      return this.selectedModelFromStore?.title || ""
    },
    // В режиме своей модели требуем все поля модели + минимум одно фото.
    customModelValid() {
      const m = this.customModel
      return (
        Boolean(m.title.trim()) &&
        Boolean(m.article.trim()) &&
        Boolean(m.brand.trim()) &&
        Boolean(m.category) &&
        Boolean(m.colour.trim()) &&
        Array.isArray(m.images) &&
        m.images.length > 0
      )
    },
    canSubmit() {
      const hasModelTitle = Boolean(this.displayModelTitle?.trim())
      // В режиме своей модели modelId ещё нет (модель создаётся этим запросом).
      const modelId = Number(this.selectedModelFromStore?.id)
      const hasModelId =
        this.customModelMode || (!isNaN(modelId) && modelId > 0)

      // Проверяем, что есть хотя бы один вариант
      if (this.variants.length === 0) {
        return false
      }

      // В режиме своей модели — обязательны поля модели.
      if (this.customModelMode && !this.customModelValid) {
        return false
      }

      // Проверяем, что ВСЕ варианты полностью заполнены
      const allVariantsValid = this.variants.every((variant) =>
        this.isVariantValid(variant)
      )

      // Проверяем кастомные изображения для всех вариантов
      const allVariantsHaveCustomImages = this.variants.every((variant) => {
        // Если тоггл "Кастомные фото" включен, должны быть оба изображения
        if (variant.isCustom) {
          return Boolean(variant.customImage1 && variant.customImage2)
        }
        // Если тоггл выключен, проверка не требуется
        return true
      })

      return (
        hasModelTitle &&
        hasModelId &&
        allVariantsValid &&
        allVariantsHaveCustomImages
      )
    },
    selectedModelFromStore() {
      return this.$store.state.selectedModel
    },
    modelImages() {
      const model = this.selectedModelFromStore
      if (!model) return []
      // Поддерживаем разные форматы: images (массив) или может быть одной строкой
      if (Array.isArray(model.images)) {
        return model.images
      }
      if (model.images) {
        return [model.images]
      }
      return []
    },
  },
  watch: {
    selectedModelFromStore: {
      handler(model) {
        if (model) {
          // modelTitle больше не храним в form — он computed (displayModelTitle).
          // Сохраняем только modelId (для обычного пути отправки) и avg_prices.
          this.form.modelId = model.id || model.model_id || null
          this.avgPrices = model.avg_prices || null
        } else {
          this.form.modelId = null
          this.avgPrices = null
        }
      },
      immediate: true,
    },
    canSubmit() {
      this.updateMainButtonState()
    },
    isSubmitting() {
      this.updateMainButtonState()
    },
    variants: {
      handler() {
        this.updateMainButtonState()
      },
      deep: true,
    },
    // Поля «своей модели» влияют на canSubmit (через customModelValid), но
    // watcher на computed canSubmit не всегда успевает подтолкнуть Telegram
    // MainButton. Дублируем явным deep-watcher — как для variants.
    customModel: {
      handler() {
        this.updateMainButtonState()
      },
      deep: true,
    },
  },
  mounted() {
    // Создаем начальный вариант с включенным тогглом "Возможность торга"
    if (this.variants.length === 0) {
      this.variants.push(createVariant())
    }
    this.setupMainButton()
    this.updateMainButtonState()
    document.addEventListener("click", this.handleDocumentClick)
    window.addEventListener("scroll", this.handleScroll, true)
  },
  beforeUnmount() {
    this.destroyMainButton()
    document.removeEventListener("click", this.handleDocumentClick)
    window.removeEventListener("scroll", this.handleScroll, true)
    // Очищаем URL preview для кастомных изображений
    this.variants.forEach((variant) => {
      if (variant.customImage1 && variant.customImage1 instanceof File) {
        URL.revokeObjectURL(URL.createObjectURL(variant.customImage1))
      }
      if (variant.customImage2 && variant.customImage2 instanceof File) {
        URL.revokeObjectURL(URL.createObjectURL(variant.customImage2))
      }
    })
  },
  methods: {
    getSizeTypeLabel(type) {
      const t = String(type || "").toUpperCase()
      return t
    },
    openModelSelect() {
      // В режиме «своя модель» клик по полю предлагает вернуться к поиску
      // по справочнику (сбросив введённые поля модели).
      if (this.customModelMode) {
        const tg = window.Telegram?.WebApp
        const confirm = () => {
          this.resetCustomModel()
          this.$store.commit("setCustomModelMode", false)
          const query = {}
          if (this.displayModelTitle) {
            query.q = this.displayModelTitle
          }
          this.$router.push({ name: "SelectModel", query })
        }
        if (tg?.showConfirm) {
          tg.showConfirm(
            "Вернуться к выбору модели из справочника? Введённые поля своей модели будут сброшены.",
            confirm,
          )
        } else if (
          window.confirm(
            "Вернуться к выбору модели из справочника? Введённые поля своей модели будут сброшены.",
          )
        ) {
          confirm()
        }
        return
      }
      const query = {}
      if (this.displayModelTitle) {
        query.q = this.displayModelTitle
      }
      this.$router.push({ name: "SelectModel", query })
    },
    // Сброс полей «своей модели» (при выходе из режима или после отправки).
    resetCustomModel() {
      // Очищаем blob-URL превью.
      if (Array.isArray(this.customModel.images)) {
        this.customModel.images.forEach((f) => {
          if (f instanceof File || f instanceof Blob) {
            URL.revokeObjectURL(URL.createObjectURL(f))
          }
        })
      }
      this.customModel = {
        title: "",
        article: "",
        brand: "",
        category: "",
        colour: "",
        images: [],
      }
    },
    // --- Фото модели (UI как в «Кастомные фото»: квадратные превью с +) --- //
    triggerModelImageInput() {
      const ref = this.$refs.modelImageInput
      if (ref) ref.click()
    },
    handleModelImageChange(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ""
      if (!files.length) return
      // лимит количества фото модели — 5 (как у товаров).
      const current = Array.isArray(this.customModel.images)
        ? this.customModel.images
        : []
      const merged = [...current, ...files].slice(0, 5)
      this.customModel.images = merged
    },
    removeModelImage(index) {
      if (!Array.isArray(this.customModel.images)) return
      this.customModel.images.splice(index, 1)
    },
    addNewVariant(focusTarget = "size") {
      this.variants.push(createVariant())
      this.$nextTick(() => {
        const focusLater = () => {
          const refsMap = {
            size: this.$refs.sizeInputs,
            count: this.$refs.countInputs,
            price: this.$refs.priceInputs,
          }
          const list = refsMap[focusTarget]
          if (!list) {
            return
          }
          const el = Array.isArray(list) ? list[list.length - 1] : list
          if (el && typeof el.focus === "function") {
            el.focus()
            if (typeof el.setSelectionRange === "function") {
              const len = el.value?.length || 0
              el.setSelectionRange(len, len)
            }
          }
        }
        window.requestAnimationFrame(() => {
          window.setTimeout(focusLater, 0)
        })
      })
    },
    removeVariant(index) {
      this.variants.splice(index, 1)
    },
    normalizeSize(variant) {
      // Если поле пустое, не валидируем
      if (!variant.size || variant.size.trim() === "") {
        this.hideSizeAlert(variant)
        return
      }

      const sizeType = variant.size_type || "EU"
      const limits = getSizeLimits(sizeType)

      if (limits.min === null || limits.max === null) {
        // Если не удалось определить лимиты, не валидируем
        this.hideSizeAlert(variant)
        return
      }

      // Проверяем, валиден ли размер для текущего типа
      if (isValidSize(variant.size, sizeType)) {
        this.hideSizeAlert(variant)
        return
      }

      // Если размер не валиден, находим ближайший корректный размер из сетки
      const closestSize = findClosestSize(variant.size, sizeType)

      if (closestSize !== null) {
        // Округляем до ближайшего корректного размера
        const oldSize = variant.size
        variant.size = String(closestSize)

        // Показываем модалку о том, что размер был округлен
        if (String(oldSize) !== String(closestSize)) {
          const tg = window.Telegram?.WebApp
          if (tg?.showPopup) {
            tg.showPopup({
              title: "Размер округлен",
              message: `Размер "${oldSize}" был округлен до ближайшего корректного значения "${closestSize}"`,
              buttons: [{ type: "close", text: "Ок" }],
            })
          } else {
            window.alert(
              `Размер "${oldSize}" был округлен до ближайшего корректного значения "${closestSize}"`
            )
          }
        }

        this.hideSizeAlert(variant)
      } else {
        // Если не удалось найти ближайший размер, показываем ошибку
        this.showSizeAlert(variant)
      }
    },
    getSizePlaceholder(sizeType) {
      const type = (sizeType || "EU").toUpperCase()
      const limits = getSizeLimits(type)

      if (limits.min === null || limits.max === null) {
        return "12"
      }

      // Для числовых типов возвращаем пример среднего значения
      if (type === "EU" || type === "RU" || type === "CM") {
        const midValue = (limits.min + limits.max) / 2
        const closest = findClosestSize(midValue, type)
        return closest !== null ? String(closest) : String(midValue)
      }

      // Для строковых типов возвращаем первый валидный размер
      const key = type === "UK" ? "uk" : "us"
      const firstEntry = SIZE_CHART.find((entry) => entry[key] != null)
      return firstEntry ? String(firstEntry[key]) : "12"
    },
    getSizeAlertMessage(sizeType) {
      const type = (sizeType || "EU").toUpperCase()
      const limits = getSizeLimits(type)

      if (limits.min === null || limits.max === null) {
        return "Введите корректный размер"
      }

      return `Размер можно ввести от ${limits.min} до ${limits.max} включительно`
    },
    openSizeTypeDropdown(variantId, event) {
      if (this.openDropdownId === variantId) {
        // Закрываем с анимацией
        this.closeSizeTypeDropdown(variantId)
        return
      }
      // Если другая модалка открыта, закрываем её
      if (this.openDropdownId !== null) {
        this.closeSizeTypeDropdown(this.openDropdownId)
        // Ждем завершения анимации закрытия
        setTimeout(() => {
          this.openSizeTypeDropdown(variantId, event)
        }, 200)
        return
      }
      this.dropdownClosingId = null
      this.openDropdownId = variantId
      if (event && event.target) {
        const rect = event.target.getBoundingClientRect()
        this.dropdownPosition = {
          top: rect.bottom + 4,
          left: rect.left,
        }
      }
    },
    closeSizeTypeDropdown(variantId) {
      if (this.openDropdownId === variantId) {
        this.dropdownClosingId = variantId
        setTimeout(() => {
          this.openDropdownId = null
          this.dropdownClosingId = null
        }, 200) // Время анимации закрытия
      }
    },
    selectSizeType(variantId, type) {
      const variant = this.variants.find((v) => v.id === variantId)
      if (variant) {
        const oldType = variant.size_type || "EU"
        const oldSize = variant.size

        // Если есть введенный размер, конвертируем его в новый тип
        if (oldSize && oldSize.trim() !== "") {
          // Сначала пытаемся конвертировать напрямую
          let convertedSize = convertSize(oldSize, oldType, type)

          // Если прямой конвертации нет, конвертируем через EU
          if (convertedSize === null && oldType.toUpperCase() !== "EU") {
            const euSize = convertSize(oldSize, oldType, "EU")
            if (euSize !== null) {
              convertedSize = convertSize(euSize, "EU", type)
            }
          }

          // Если конвертация удалась, обновляем размер
          if (convertedSize !== null) {
            variant.size = String(convertedSize)
            this.hideSizeAlert(variant)
          } else {
            // Если конвертация не удалась, находим ближайший валидный размер для нового типа
            const closestSize = findClosestSize(oldSize, type)
            if (closestSize !== null) {
              variant.size = String(closestSize)
              this.hideSizeAlert(variant)
            }
          }
        }

        variant.size_type = type
      }
      if (this.openDropdownId === variantId) {
        this.closeSizeTypeDropdown(variantId)
      }
    },
    handleDocumentClick(event) {
      if (!event.target.closest(`.${this.$style.sizeTypeWrapper}`)) {
        if (this.openDropdownId !== null) {
          this.closeSizeTypeDropdown(this.openDropdownId)
        }
      }
    },
    handleScroll() {
      // Закрываем дропдаун при скролле
      if (this.openDropdownId !== null) {
        this.closeSizeTypeDropdown(this.openDropdownId)
      }
    },
    showSizeAlert(variant) {
      variant.showSizeAlert = true
      // Автоматически скрываем алерт через 3 секунды
      setTimeout(() => {
        this.hideSizeAlert(variant)
      }, 3000)
    },
    hideSizeAlert(variant) {
      variant.showSizeAlert = false
    },
    normalizeNumber(value) {
      if (value === null || value === undefined) return 0
      const normalized = String(value)
        .replace(/[\s\u00A0]/g, "")
        .replace(/,/g, ".")
        .replace(/[^\d.]/g, "")
      if (!normalized) {
        return 0
      }
      return Number(normalized)
    },
    isVariantValid(variant) {
      const sizeFilled = Boolean((variant.size || "").trim())
      const count = this.normalizeNumber(variant.count)
      const price = this.normalizeNumber(variant.price)
      return sizeFilled && variant.size_type && count > 0 && price > 0
    },
    async handleSubmit() {
      if (!this.canSubmit || this.isSubmitting) return

      this.isSubmitting = true

      try {
        // Режим «своя модель»: один составной запрос POST /model/add
        // (создаёт pending-модель + товар). is_trade/has_box берём из варианта.
        if (this.customModelMode) {
          const variant = this.variants[0]
          // Конвертация размера в EU — как в обычном пути.
          let euSize = (variant.size || "").trim()
          const currentSizeType = variant.size_type || "EU"
          if (currentSizeType.toUpperCase() !== "EU") {
            const converted = convertSize(euSize, currentSizeType, "EU")
            if (converted !== null) {
              euSize = String(converted)
            } else {
              const closest = findClosestSize(euSize, currentSizeType)
              if (closest !== null) {
                const euConverted = convertSize(
                  String(closest),
                  currentSizeType,
                  "EU",
                )
                if (euConverted !== null) {
                  euSize = String(euConverted)
                }
              }
            }
          }

          const m = this.customModel
          const formData = new FormData()
          formData.append("title", m.title.trim())
          formData.append("article", m.article.trim())
          formData.append("brand", m.brand.trim())
          formData.append("category", m.category)
          formData.append("colour", m.colour.trim())
          formData.append("price", this.normalizeNumber(variant.price))
          formData.append("size", euSize)
          formData.append("count", this.normalizeNumber(variant.count))
          formData.append("is_trade", Boolean(variant.allowTrade))
          formData.append("has_box", Boolean(variant.hasBox))
          m.images.forEach((f) => formData.append("images", f))

          await this.$store.dispatch("createCustomModel", formData)

          try {
            await this.$store.dispatch("getProfile")
          } catch (e) {
            window.console.error(
              "Не удалось обновить профиль после создания модели",
              e,
            )
          }

          this.notifyCustomModelSuccess()
          this.$store.commit("setSelectedModel", null)
          this.$store.commit("setCustomModelMode", false)
          this.resetCustomModel()
          this.$router.replace({ name: "MyProducts" })
          return
        }

        // Обычный путь: отправляем каждый вариант отдельным запросом
        // Отправляем каждый вариант отдельным запросом
        for (const variant of this.variants) {
          // Конвертируем размер в EU для отправки на сервер
          let euSize = variant.size.trim()
          const currentSizeType = variant.size_type || "EU"

          if (currentSizeType.toUpperCase() !== "EU") {
            const converted = convertSize(euSize, currentSizeType, "EU")
            if (converted !== null) {
              euSize = String(converted)
            } else {
              // Если конвертация не удалась, пытаемся найти ближайший размер
              const closest = findClosestSize(euSize, currentSizeType)
              if (closest !== null) {
                const euConverted = convertSize(
                  String(closest),
                  currentSizeType,
                  "EU"
                )
                if (euConverted !== null) {
                  euSize = String(euConverted)
                }
              }
            }
          }

          // Всегда отправляем через FormData
          const formData = new FormData()
          formData.append("model_id", Number(this.form.modelId))
          formData.append("price", this.normalizeNumber(variant.price))
          formData.append("size", euSize)
          formData.append("count", this.normalizeNumber(variant.count))
          formData.append("has_box", Boolean(variant.hasBox))
          formData.append("is_custom", Boolean(variant.isCustom))
          formData.append("is_trade", Boolean(variant.allowTrade))

          // Добавляем кастомные изображения только если они есть
          if (
            variant.isCustom &&
            variant.customImage1 instanceof File &&
            variant.customImage2 instanceof File
          ) {
            formData.append("custom_images", variant.customImage1)
            formData.append("custom_images", variant.customImage2)
          }

          await this.$store.dispatch("addUserProduct", formData)
        }

        try {
          await this.$store.dispatch("getProfile")
        } catch (e) {
          window.console.error("Не удалось обновить профиль после добавления", e)
        }

        this.notifySuccess()
        this.$store.commit("setSelectedModel", null)
        this.$router.replace({ name: "MyProducts" })
      } catch (error) {
        // Для режима «своя модель» — спец-обработка 409 (артикул занят).
        if (this.customModelMode && error?.response?.status === 409) {
          this.notifyArticleConflict()
        } else {
          this.notifyError()
        }
      } finally {
        this.isSubmitting = false
        this.updateMainButtonState()
      }
    },
    notifySuccess() {
      const tg = window.Telegram?.WebApp
      if (tg?.showPopup) {
        tg.showPopup({
          title: "Готово",
          message: "Позиция отправлена на модерацию.",
          buttons: [{ type: "close", text: "Ок" }],
        })
      } else {
        window.alert("Позиция отправлена на модерацию.")
      }
    },
    notifyCustomModelSuccess() {
      const tg = window.Telegram?.WebApp
      if (tg?.showPopup) {
        tg.showPopup({
          title: "Отправлено",
          message:
            "Товар с новой моделью отправлен на модерацию. " +
            "После проверки админом модель появится в каталоге.",
          buttons: [{ type: "close", text: "Ок" }],
        })
      } else {
        window.alert(
          "Товар с новой моделью отправлен на модерацию. " +
            "После проверки админом модель появится в каталоге.",
        )
      }
    },
    notifyArticleConflict() {
      const tg = window.Telegram?.WebApp
      const msg =
        "Модель с таким артикулом уже существует. " +
        "Измените артикул или выберите существующую модель в справочнике."
      if (tg?.showPopup) {
        tg.showPopup({
          title: "Артикул занят",
          message: msg,
          buttons: [{ type: "close", text: "Понятно" }],
        })
      } else {
        window.alert(msg)
      }
    },
    notifyError() {
      const tg = window.Telegram?.WebApp
      if (tg?.showPopup) {
        tg.showPopup({
          title: "Ошибка",
          message: "Не удалось отправить товар. Попробуйте позже.",
          buttons: [{ type: "close", text: "Понятно" }],
        })
      } else {
        window.alert("Не удалось отправить товар. Попробуйте позже.")
      }
    },
    setupMainButton() {
      const tg = window.Telegram?.WebApp
      const mb = tg?.MainButton
      if (!mb) {
        this.hasTelegramMainButton = false
        return
      }
      this.hasTelegramMainButton = true
      this.mainButtonInstance = mb
      if (this.mbHandler) {
        mb.offClick(this.mbHandler)
      }
      this.mbHandler = () => this.handleSubmit()
      mb.onClick(this.mbHandler)
      mb.textColor = "#ffffff"
      mb.color = "#e65c00"
      this.updateMainButtonState()
      mb.show()
    },
    updateMainButtonState() {
      const mb = this.mainButtonInstance || window.Telegram?.WebApp?.MainButton
      if (!mb) return
      const text = this.isSubmitting
        ? "Отправляем..."
        : "Отправить на модерацию"
      if (typeof mb.setText === "function") {
        mb.setText(text)
      } else {
        mb.text = text
      }
      const disabled = this.isSubmitting || !this.canSubmit
      const activeColor = "#e65c00"
      const disabledColor = "#d7d8dd"
      if (disabled) {
        mb.disable?.()
        mb.color = disabledColor
      } else {
        mb.enable?.()
        mb.color = activeColor
      }
      mb.textColor = "#ffffff"
      if (this.hasTelegramMainButton) {
        mb.show?.()
      }
    },
    destroyMainButton() {
      const mb = this.mainButtonInstance || window.Telegram?.WebApp?.MainButton
      if (mb && this.mbHandler) {
        mb.offClick(this.mbHandler)
      }
      if (mb) {
        mb.hide?.()
      }
      this.mbHandler = null
      this.mainButtonInstance = null
      this.hasTelegramMainButton = false
    },
    openFaq() {
      this.$router.push({ name: "Faq" })
    },
    hasSize(variant) {
      return (
        variant.size && String(variant.size).trim() !== "" && variant.size_type
      )
    },
    getAvgPrice(variant) {
      if (!this.avgPrices || !this.hasSize(variant)) {
        return null
      }

      const sizeStr = String(variant.size).trim()
      const sizeType = variant.size_type || "EU"
      let euSize = null

      // Если размер уже в EU, используем его напрямую
      if (sizeType.toUpperCase() === "EU") {
        euSize = sizeStr
      } else {
        // Конвертируем размер в EU
        euSize = convertSize(sizeStr, sizeType, "EU")
        if (euSize === null) {
          return null
        }
        euSize = String(euSize)
      }

      // Ищем среднюю цену для этого размера
      const avgPrice = this.avgPrices[euSize]
      return avgPrice !== null && avgPrice !== undefined
        ? Number(avgPrice)
        : null
    },
    formatPrice(value) {
      if (!value) return ""
      return Number(value).toLocaleString("ru-RU")
    },
    triggerImageInput(variant, imageNumber) {
      const inputRef = this.$refs[`customImage${imageNumber}_${variant.id}`]
      if (inputRef && inputRef[0]) {
        inputRef[0].click()
      } else if (inputRef) {
        inputRef.click()
      }
    },
    handleCustomImageChange(variant, imageNumber, event) {
      const file = event.target.files?.[0]
      if (file) {
        if (imageNumber === 1) {
          variant.customImage1 = file
        } else {
          variant.customImage2 = file
        }
        // Сбрасываем значение инпута, чтобы можно было выбрать тот же файл повторно
        event.target.value = ""
      }
    },
    removeCustomImage(variant, imageNumber) {
      if (imageNumber === 1) {
        variant.customImage1 = null
      } else {
        variant.customImage2 = null
      }
    },
    getImagePreview(file) {
      if (!file) return ""
      if (typeof file === "string") {
        // Если это URL строки
        return file
      }
      // Если это объект с полем url, возвращаем url
      if (file && typeof file === "object" && file.url) {
        return file.url
      }
      // Если это File объект, создаем preview URL
      if (file instanceof File || file instanceof Blob) {
        return URL.createObjectURL(file)
      }
      // Если ничего не подошло, возвращаем пустую строку
      return ""
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 0.625rem 1rem 6rem 1rem;
  min-height: 100vh;
  background: #f5f6fb;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sectionLabel {
  padding-left: 1rem;
  font-size: 0.875rem;
  color: #6d6d72;
  font-weight: 400;
}

.galleryWrap {
  position: relative;
  margin-bottom: 1rem;
  background: #fff;
  border-radius: 0.625rem;
  padding: 1rem 0;
  img {
    transform: scale(-1, 1);
  }
}

/* «Своя модель»: блок полей под полем выбора модели */
.customModelSection {
  background: #fff;
  border-radius: 0.625rem;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.customModelHeader {
  font-size: 1rem;
  font-weight: 700;
  color: #000;
}

.inputCard {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
}

.modelInput {
  width: 100%;
  border: none;
  background: none;
  font-size: 1rem;
  color: #8e8e93;
  outline: none;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modelInput[value]:not([value=""]) {
  color: #000;
}

.variantsSection {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.variantCard {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.25rem 1rem 0.25rem 1rem;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
}

.variantTopRow {
  display: grid;
  grid-template-columns: min-content min-content 1fr auto;
  align-items: center;
  padding: 0.625rem 0;
  gap: 0.75rem;
  position: relative;
  overflow: visible;
}

.variantInputBlock {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  &:first-child {
    .invisibleInput {
      min-width: 1.25rem;
      width: auto;
      max-width: 4rem;
      display: inline-block;
    }
  }
}

.combinedInput {
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
  font-size: 1rem;
  color: #000;
  white-space: nowrap;
  position: relative;
}
.combinedInput span {
  color: #000;
  font-weight: 400;
}

.invisibleInput {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-size: 1rem;
  color: #000;
  outline: none;
  font-weight: 400;
  max-width: 2rem;
}

.sizeInputWrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.sizeInput {
  text-align: right;
}

.sizeAlert {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.25rem;
  padding: 0.375rem 0.5rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: #856404;
  white-space: nowrap;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.countInput {
  text-align: right;
}

.addVariantSize {
  text-align: right;
  justify-self: end;
}

.countCombined {
  min-width: 3.5rem;
}

.addVariantCount {
  text-align: right;
  justify-self: end;
}

.priceInput {
  width: 100%;
  max-width: 100%;
  text-align: right;
}

.priceCombined {
  justify-content: flex-end;
  width: 100%;
}

.priceBlock {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-end;
}

.avgPriceHint {
  font-size: 0.75rem;
  color: #8e8e93;
  text-align: right;
}

.sizeTypeWrapper {
  position: relative;
  display: inline-block;
}

.sizeTypeWrapper {
  position: relative;
  display: inline-block;
}

.sizeTypeLabel {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.sizeTypeArrow {
  width: 0.75rem;
  height: 0.5rem;
  color: currentColor;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.sizeTypeArrowOpen {
  transform: rotate(180deg);
}

.sizeTypeDropdown {
  position: fixed;
  background: #fff;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  min-width: 4rem;
  display: flex;
  flex-direction: column;
  opacity: 1;
  transform: translateY(0) scale(1);
  transform-origin: top left;
  transition: opacity 0.2s ease, transform 0.2s ease;
  animation: dropdownOpen 0.2s ease forwards;
}

.sizeTypeDropdownClosing {
  animation: dropdownClose 0.2s ease forwards;
}

@keyframes dropdownOpen {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes dropdownClose {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }
}

.sizeTypeOption {
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 1rem;
  color: #000;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
  display: block;

  &:hover {
    background: #f5f5f5;
  }

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
}

.sizeTypeOptionActive {
  background: #f5f5f5;
  font-weight: 600;
}

.hiddenSelect {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.removeBtn {
  padding: 0;
  width: max-content;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
  }
}

.variantDivider {
  height: 1px;
  background: #f0f0f5;
  margin: 0 -1rem;
}

.variantBottomRow {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.4rem 0;
}

.variantBottomItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.variantBottomRow span {
  font-size: 1rem;
  color: #000;
}

/* Кнопка добавления варианта */
.addVariantBtn {
  box-sizing: border-box;
  width: 100%;
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  border: none;
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  color: #8e8e93;
  font-size: 1rem;
}

.addVariantPrice {
  text-align: right;
  justify-self: end;
}

.clickableLabel {
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  align-items: center;
}

.switchGroup {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0 1rem;
  margin-top: 1rem;
}

.switchRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0;
  font-size: 1rem;
  color: #000;
}

.groupDivider {
  height: 1px;
  background: #f0f0f5;
  margin: 0;
}

.toggle {
  position: relative;
  width: 3.2rem;
  height: 1.9rem;
  cursor: pointer;
  display: inline-block;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle span {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9e9ea;
  transition: 0.3s;
  border-radius: 2rem;
}

.toggle span:before {
  position: absolute;
  content: "";
  height: 1.7rem;
  width: 1.7rem;
  left: 0.1rem;
  bottom: 0.1rem;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle input:checked + span {
  background-color: #000;
}
.toggleBlack input:checked + span {
  background-color: #000;
}

.toggle input:checked + span:before {
  transform: translateX(1.3rem);
}

.links {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.links button {
  border: none;
  background: none;
  color: #8e8e93;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem;
}
.arrowIcon {
  font-size: 1.2rem;
  line-height: 1;
  margin-top: -2px;
}

.submitBar {
  margin-top: 1rem;
}
.submitBtn {
  width: 100%;
  background: #ff6600;
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
}
.submitBtn:disabled {
  opacity: 0.55;
  box-shadow: none;
}

.customImagesSection {
  margin-top: 1rem;
  padding: 1rem 0;
  border-top: 1px solid #f0f0f5;
}

.customImagesLabel {
  font-size: 0.875rem;
  color: #6d6d72;
  margin-bottom: 0.75rem;
  padding-left: 0;
}

.customImagesRequirements {
  font-size: 0.8125rem;
  color: #8e8e93;
  line-height: 1.4;
  margin: 0 0 0.75rem 0;
  padding: 0;
}

.customImagesGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.customImageWrapper {
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;
}

.customImagePreview {
  position: relative;
  width: 100%;
  height: 100%;
}

.customImagePreview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.removeImageBtn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.removeImageBtn img {
  width: 1rem;
  height: 1rem;
  filter: brightness(0) invert(1);
}

.addImageBtn {
  width: 100%;
  height: 100%;
  border: 2px dashed #d1d1d6;
  border-radius: 0.5rem;
  background: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8e8e93;
  transition: all 0.2s;
}

.addImageBtn:hover {
  border-color: #8e8e93;
  background: #f5f5f5;
}

.addImageBtn svg {
  width: 2rem;
  height: 2rem;
}

.hiddenInput {
  display: none;
}
</style>
